import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  IoCheckmark,
  IoRefreshOutline,
  IoChevronBackOutline,
  IoChevronForwardOutline,
  IoTimeOutline,
} from "react-icons/io5";
import {
  resolveFeedbackItem,
  resubmitFormAfterFeedback,
  unresolveFeedbackItem,
  type FeedbackBatchDetail,
  type FeedbackItemDetail,
} from "../../../API/feedbackAPI";

function formatMessageTime(
  dateInput: string | number | Date | null | undefined
): string {
  if (dateInput == null || dateInput === "") return "Recently";
  try {
    const d = dateInput instanceof Date ? dateInput : new Date(dateInput);
    if (Number.isNaN(d.getTime())) return "Recently";
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    const formatted = d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
    return formatted === "Invalid Date" ? "Recently" : formatted;
  } catch {
    return "Recently";
  }
}

function reviewerLabel(batch: FeedbackBatchDetail | null, item: FeedbackItemDetail): string {
  const email = item.adminEmail || batch?.submittedByAdminEmail;
  if (!email) return "Your reviewer";
  const local = email.split("@")[0] || email;
  const parts = local.replace(/[._]/g, " ").split(" ").filter(Boolean);
  if (parts.length === 0) return "Your reviewer";
  return parts.map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()).join(" ");
}

function reviewerInitials(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

interface FeedbackTabProps {
  applicationId: number | undefined;
  formId: number | undefined;
  /** e.g. current section name for the pink banner */
  activeCategoryTitle?: string | null;
  batch: FeedbackBatchDetail | null;
  loading?: boolean;
  onBatchUpdated: (batch: FeedbackBatchDetail | null) => void;
  /** Scroll main form to this item's question and highlight it. */
  onNavigateToSource?: (item: FeedbackItemDetail) => void;
  highlightedItemId?: number | null;
  /** Called before resolve / resubmit to persist the current section's answers. */
  onSaveBeforeFeedbackAction?: () => Promise<void>;
  /** Notifies parent which feedback item is currently active (for inline card). */
  onActiveFeedbackItemChange?: (item: FeedbackItemDetail | null) => void;
}

const FeedbackTab: React.FC<FeedbackTabProps> = ({
  applicationId,
  formId,
  activeCategoryTitle,
  batch,
  loading = false,
  onBatchUpdated,
  onNavigateToSource,
  highlightedItemId,
  onSaveBeforeFeedbackAction,
  onActiveFeedbackItemChange,
}) => {
  const [busyItemId, setBusyItemId] = useState<number | null>(null);
  const [unresolveItemId, setUnresolveItemId] = useState<number | null>(null);
  const [resubmitting, setResubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeItemIndex, setActiveItemIndex] = useState(0);
  /** Item awaiting user confirmation before calling resolve API */
  const [resolveConfirmItem, setResolveConfirmItem] =
    useState<FeedbackItemDetail | null>(null);

  const resolveInFlight =
    resolveConfirmItem != null && busyItemId === resolveConfirmItem.id;

  useEffect(() => {
    if (!resolveConfirmItem) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !resolveInFlight) setResolveConfirmItem(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [resolveConfirmItem, resolveInFlight]);

  const sortedItems = useMemo(() => {
    if (!batch?.items?.length) return [];
    return [...batch.items].sort((a, b) => a.orderIndex - b.orderIndex);
  }, [batch]);

  // Clamp activeItemIndex when items shrink
  useEffect(() => {
    if (sortedItems.length === 0) return;
    setActiveItemIndex((prev) => Math.min(prev, sortedItems.length - 1));
  }, [sortedItems.length]);

  // Sync activeItemIndex when parent changes the highlighted item (e.g., user clicks question icon)
  useEffect(() => {
    if (highlightedItemId == null || sortedItems.length === 0) return;
    const idx = sortedItems.findIndex((i) => i.id === highlightedItemId);
    if (idx !== -1) {
      setActiveItemIndex((prev) => (prev !== idx ? idx : prev));
    }
  }, [highlightedItemId, sortedItems]);

  // Keep a stable ref to the active-item change callback to avoid stale closures
  const onActiveFeedbackItemChangeRef = useRef(onActiveFeedbackItemChange);
  onActiveFeedbackItemChangeRef.current = onActiveFeedbackItemChange;

  // Notify parent whenever active item changes (drives inline card in StepperForm)
  useEffect(() => {
    const item = sortedItems[activeItemIndex] ?? null;
    onActiveFeedbackItemChangeRef.current?.(item);
  }, [activeItemIndex, sortedItems]);

  const goToIndex = useCallback(
    (newIndex: number) => {
      if (newIndex < 0 || newIndex >= sortedItems.length) return;
      setActiveItemIndex(newIndex);
      const item = sortedItems[newIndex];
      if (item) {
        onNavigateToSource?.(item);
      }
    },
    [sortedItems, onNavigateToSource]
  );

  const allResolved = useMemo(
    () => sortedItems.length > 0 && sortedItems.every((i) => i.status === "resolved"),
    [sortedItems]
  );

  const appStage = batch?.application?.stage;
  const canResubmit =
    allResolved && appStage === "revision_requested" && applicationId && formId;

  const performResolve = useCallback(
    async (item: FeedbackItemDetail) => {
      if (!applicationId || item.status === "resolved") return;
      setBusyItemId(item.id);
      setError(null);
      try {
        // Ensure latest section answers hit the API before resolve; avoids “resolved” on stale form state.
        if (onSaveBeforeFeedbackAction) {
          try {
            await onSaveBeforeFeedbackAction();
          } catch {
            setError("Could not save your changes. Please try again.");
            setResolveConfirmItem(null);
            return;
          }
        }
        const updated = await resolveFeedbackItem(applicationId, item.id);
        onBatchUpdated(updated);
        setResolveConfirmItem(null);
      } catch (e) {
        setError((e as Error)?.message || "Could not update item");
      } finally {
        setBusyItemId(null);
      }
    },
    [applicationId, onBatchUpdated, onSaveBeforeFeedbackAction]
  );

  const handleUnresolve = useCallback(
    async (item: FeedbackItemDetail) => {
      if (!applicationId) return;
      setUnresolveItemId(item.id);
      setError(null);
      try {
        const updated = await unresolveFeedbackItem(applicationId, item.id);
        onBatchUpdated(updated);
      } catch (e) {
        setError((e as Error)?.message || "Could not undo resolution");
      } finally {
        setUnresolveItemId(null);
      }
    },
    [applicationId, onBatchUpdated]
  );

  const handleResubmit = useCallback(async () => {
    if (!applicationId || !formId) return;
    setResubmitting(true);
    setError(null);
    try {
      // Auto-save before resubmit so edits made while the feedback drawer was open are included.
      if (onSaveBeforeFeedbackAction) {
        try {
          await onSaveBeforeFeedbackAction();
        } catch {
          setError("Could not save your changes. Please try again before submitting.");
          return;
        }
      }
      await resubmitFormAfterFeedback(applicationId, formId);
      onBatchUpdated(null);
    } catch (e) {
      setError((e as Error)?.message || "Could not submit");
    } finally {
      setResubmitting(false);
    }
  }, [applicationId, formId, onBatchUpdated, onSaveBeforeFeedbackAction]);

  if (!applicationId || !formId) {
    return (
      <div className="text-[#4B5563] text-sm py-4">No application or form selected.</div>
    );
  }

  if (loading && !batch) {
    return (
      <div className="py-8 flex justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1B365D]" />
      </div>
    );
  }

  if (!batch || sortedItems.length === 0) {
    return (
      <div className="flex-1 min-h-0 md:overflow-y-auto">
        <p className="text-[#4B5563] text-sm py-2">
          No reviewer feedback on this form yet. When your attorney leaves notes, they will appear
          here.
        </p>
      </div>
    );
  }

  const sectionLabel = activeCategoryTitle?.trim()
    ? `Attorney Review: ${activeCategoryTitle}`
    : "Attorney Review";

  return (
    <div className="flex flex-1 min-h-0 flex-col">
      {resolveConfirmItem && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4"
          role="presentation"
          onClick={() => {
            if (!resolveInFlight) setResolveConfirmItem(null);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="resolve-feedback-title"
            className="w-full max-w-md rounded-xl bg-white p-5 shadow-lg border border-[#E5E7EB]"
            onClick={(e) => e.stopPropagation()}
          >
            <h2
              id="resolve-feedback-title"
              className="text-lg font-semibold text-[#1B365D] m-0 mb-2"
            >
              Mark this note as resolved?
            </h2>
            <p className="text-sm text-[#4B5563] m-0 mb-5">
              This tells your reviewer you have addressed this item, & the note
              will be hidden from you. Also, you will not be able to edit the
              answer later on.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="px-4 py-2 rounded-lg text-sm font-medium text-[#374151] border border-[#D1D5DB] hover:bg-[#F9FAFB]"
                onClick={() => {
                  if (!resolveInFlight) setResolveConfirmItem(null);
                }}
                disabled={resolveInFlight}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={busyItemId === resolveConfirmItem.id}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-[#0D9488] hover:bg-[#0F766E] disabled:opacity-60"
                onClick={() => void performResolve(resolveConfirmItem)}
              >
                {busyItemId === resolveConfirmItem.id
                  ? "Saving…"
                  : "Mark resolved"}
              </button>
            </div>
          </div>
        </div>
      )}

      {sortedItems.length > 1 && (
        <div className="flex items-center justify-between mb-3 shrink-0 bg-[#F9FAFB] rounded-lg px-2 py-1.5">
          <button
            type="button"
            disabled={activeItemIndex === 0}
            onClick={() => goToIndex(activeItemIndex - 1)}
            className="p-1.5 rounded-full hover:bg-[#E5E7EB] disabled:opacity-30 disabled:cursor-not-allowed text-[#1B365D] transition-colors"
            aria-label="Previous feedback item"
          >
            <IoChevronBackOutline className="text-base" />
          </button>
          <span className="text-xs font-medium text-[#4B5563]">
            Note {activeItemIndex + 1} of {sortedItems.length}
          </span>
          <button
            type="button"
            disabled={activeItemIndex === sortedItems.length - 1}
            onClick={() => goToIndex(activeItemIndex + 1)}
            className="p-1.5 rounded-full hover:bg-[#E5E7EB] disabled:opacity-30 disabled:cursor-not-allowed text-[#1B365D] transition-colors"
            aria-label="Next feedback item"
          >
            <IoChevronForwardOutline className="text-base" />
          </button>
        </div>
      )}

      {error && (
        <p className="text-red-600 text-sm mb-3" role="alert">
          {error}
        </p>
      )}

      <div className="flex-1 space-y-4 px-1 pt-1 pb-4 min-h-0 overflow-y-auto">
        {sortedItems.map((item) => {
          const resolved = item.status === "resolved";
          const name = reviewerLabel(batch, item);
          const isDoc = item.documentId != null && !item.questionId;
          const calloutTitle = resolved
            ? "Addressed"
            : isDoc
              ? "Action required"
              : "Important note";
          const calloutBg = resolved
            ? "bg-[#E8F8F2] border-[#0D9488]"
            : "bg-[#FFF4E8] border-[#E8A54B]";
          const calloutTitleColor = resolved
            ? "text-[#0F766E]"
            : "text-[#B45309]";
          const isActive = sortedItems[activeItemIndex]?.id === item.id;
          const ring = isActive ? "ring-1 ring-[#1B365D]" : "";

          const canJumpToQuestion =
            item.questionId != null &&
            item.questionId > 0 &&
            typeof onNavigateToSource === "function";

          return (
            <div key={item.id} className="relative pt-8">
              {/* Resolve + undo controls — float above the card, fully clear of the top border */}
              {resolved && batch?.status === "submitted" && (
                <button
                  type="button"
                  disabled={unresolveItemId === item.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    void handleUnresolve(item);
                  }}
                  title="Undo resolve"
                  aria-label="Undo resolve for this feedback item"
                  className="absolute top-0 right-8 z-10 flex h-7 w-7 items-center justify-center rounded-full border-2 border-[#CBD5E1] bg-white text-[#6B7280] hover:bg-[#F3F4F6] disabled:opacity-50 disabled:cursor-wait transition-colors"
                >
                  {unresolveItemId === item.id ? (
                    <span className="text-[10px]">…</span>
                  ) : (
                    <IoRefreshOutline className="text-sm" />
                  )}
                </button>
              )}
              {resolved ? (
                <span
                  className="absolute top-0 right-0 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-[#0D9488] text-white shadow-sm"
                  role="img"
                  aria-label="Feedback resolved"
                >
                  <IoCheckmark className="text-sm" />
                </span>
              ) : (
                <button
                  type="button"
                  disabled={busyItemId === item.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    setResolveConfirmItem(item);
                  }}
                  title="Mark as resolved"
                  aria-label="Mark this feedback item as resolved"
                  className="absolute top-0 right-0 z-10 flex h-7 w-7 items-center justify-center rounded-full border-2 border-[#CBD5E1] bg-white shadow-sm hover:border-[#0D9488] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1B365D] disabled:opacity-50 disabled:cursor-wait transition-colors"
                />
              )}

              <article
                className={`rounded-xl border border-[#E5E7EB] bg-white shadow-sm ${ring}`}
              >
                <div
                  className={`min-w-0 ${canJumpToQuestion ? "cursor-pointer rounded-xl hover:bg-[#F9FAFB] transition-colors pt-4 px-4 pb-3" : "pt-4 px-4 pb-3"}`}
                  role={canJumpToQuestion ? "button" : undefined}
                  tabIndex={canJumpToQuestion ? 0 : undefined}
                  onClick={
                    canJumpToQuestion
                      ? () => onNavigateToSource!(item)
                      : undefined
                  }
                  onKeyDown={
                    canJumpToQuestion
                      ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            onNavigateToSource!(item);
                          }
                        }
                      : undefined
                  }
                  aria-label={
                    canJumpToQuestion
                      ? "Show this question in the form and highlight it"
                      : undefined
                  }
                >
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <div
                        className="h-10 w-10 rounded-full bg-[#1B365D] text-white text-xs font-semibold flex items-center justify-center shrink-0"
                        aria-hidden
                      >
                        {reviewerInitials(name)}
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold text-[#1B365D] text-sm leading-tight truncate">
                          {name}
                        </div>
                        <div className="text-xs text-[#6B7280]">
                          Immigration Attorney
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    className={`rounded-lg border px-3 py-3 mb-3 ${calloutBg}`}
                  >
                    <div
                      className={`text-xs font-bold mb-2 ${calloutTitleColor}`}
                      style={{ letterSpacing: "0.02em" }}
                    >
                      {calloutTitle}
                    </div>
                    <div className="text-sm text-[#111827] whitespace-pre-wrap break-words leading-relaxed">
                      {item.feedbackText}
                    </div>
                  </div>

                  {(item.questionContext?.text || item.documentContext) && (
                    <p className="mt-3 text-xs text-[#6B7280] border-t border-[#F3F4F6] pt-2">
                      {item.questionContext?.text
                        ? `Re: ${item.questionContext.text}`
                        : `Document: ${item.documentContext?.fileName ?? "Upload"}`}
                    </p>
                  )}

                  <div className="flex items-center gap-1.5 mt-3 text-xs text-[#9CA3AF]">
                    <IoTimeOutline className="text-base shrink-0" aria-hidden />
                    <span>{formatMessageTime(item.createdAt)}</span>
                  </div>
                  {canJumpToQuestion && (
                    <p className="mt-2 text-xs text-[#1B365D] font-medium">
                      Click to view this answer in the form
                    </p>
                  )}
                </div>
              </article>
            </div>
          );
        })}
      </div>

      <div className="border-t border-[#E5E7EB] pt-4 mt-2 shrink-0">
        <button
          type="button"
          onClick={handleResubmit}
          disabled={resubmitting || !canResubmit}
          className="w-full py-3 rounded-lg bg-[#EA580C] text-white text-sm font-semibold hover:bg-[#C2410C] disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {resubmitting
            ? "Submitting…"
            : "Resolve & Submit"}
        </button>
        <p className="text-xs text-[#6B7280] mt-2 text-center">
          {canResubmit ? (
            <>
              This sends your form back to <strong>under review</strong>.
            </>
          ) : (
            "Resolve all notes above to submit."
          )}
        </p>
      </div>
    </div>
  );
};

export default FeedbackTab;
